import { differenceInDays, formatISO } from 'date-fns';
import { useEffect } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { useDateRangeContext } from '@/context/DateRangeContext';
import { ViewportTarget, useIsInViewport } from '@/hooks/useIsInViewport';
import { DateRange } from '@/store';

/**
 * The events already reported, so that one view is one event for as long as the page is open.
 *
 * Held at module scope because a chart does not outlive its section: collapsing a section unmounts it, and a
 * view is still the same view when it is expanded again.
 */
const trackedEventIds = new Set<string>();

export const useIsViewedTracking = (
    target: ViewportTarget,
    event: { name: string; eventId: string; metadata: Record<string, unknown> } = {
        name: 'default_event_name',
        eventId: 'default_event_id',
        metadata: {},
    }
) => {
    const isVisible = useIsInViewport(target, { elapsedMs: 3000 });
    const mixpanel = useMixpanel();

    const trackEvent = () => {};

    useEffect(() => {
        const { name, eventId, metadata } = event;

        if (!isVisible || !mixpanel || trackedEventIds.has(eventId)) {
            return;
        }

        trackedEventIds.add(eventId);
        mixpanel.track(name, metadata);
    }, [isVisible, mixpanel, event]);

    return trackEvent;
};

type TrackChartViewProps = {
    containerEl: ViewportTarget;
    spotterId?: string | null;
    chartTitle: string;
    /**
     * The time window of a chart that shows less than the date range of the page. Each window is its own view.
     * Pass only a fixed window: a window that moves with the clock would send a new view each time it moves.
     */
    dateRange?: DateRange;
};

export const useTrackChartView = ({
    containerEl,
    spotterId,
    chartTitle,
    dateRange: chartDateRange,
}: TrackChartViewProps) => {
    const { startDateStamp, endDateStamp, dateRange: pageDateRange } = useDateRangeContext();
    const dateRange = chartDateRange ?? pageDateRange;
    const eventId = chartDateRange
        ? `${spotterId}-${chartTitle}-${formatISO(chartDateRange.startDate)}-${formatISO(chartDateRange.endDate)}`
        : `${spotterId}-${chartTitle}-${startDateStamp}-${endDateStamp}`;
    const metadata = {
        spotterId,
        type: chartTitle,
        ...(dateRange && {
            startDate: formatISO(dateRange.startDate),
            endDate: formatISO(dateRange.endDate),
            rangeInDays: differenceInDays(dateRange.endDate, dateRange.startDate),
        }),
    };

    return useIsViewedTracking(containerEl, {
        name: 'Chart viewed',
        eventId,
        metadata,
    });
};

export const TrackChartViewWhenRendered = (props: TrackChartViewProps) => {
    useTrackChartView(props);

    return null;
};

export const exportForTests = {
    resetTrackedEvents: () => trackedEventIds.clear(),
};
